/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { map } from 'lit/directives/map.js';
import { repeat } from 'lit/directives/repeat.js';
import { BaseProjectView } from './base-project-view.js';
import { projectInviteViewStyles } from './project-invite-view-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/copy-button/copy-button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-md-textarea/q1-md-textarea.js';
import '../../components/q1-member-chip/q1-member-chip.js';
import '../../components/q1-member-select-list/q1-member-select-list.js';
import '../../components/q1-progress-panel/q1-progress-panel.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== Q1 MODULES ===*/
import { enrichMemberSearchItem } from '../../modules/member-helpers-client.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';
import { projectMemberRoleOptions } from '../../models/project-member-client-enums.js';
import { q1VersionFunctions } from '../../modules/q1-core-functions.js';
import { TabRouteMixin } from '../../modules/tab-route-mixin.js';

/*=== CONSTANTS ===*/

const CACHE_MAX_AGE = 10; // in minutes

const MEMBER_SEARCH_CACHE = 'all-members-search';

const PROJECT_MEMBER_INVITE_FLOW = 'projectMembers.invite';

const TAB_SETTINGS = (project) => ({
  members: {
    path: `/projects/${project.key}/invite`,
    title: `Invite Members to ${project.name} - q1 Network`,
  },
});

/*=== VIEW CLASS ===*/

export class ProjectInviteView extends FormHostMixin(TabRouteMixin(BaseProjectView)) {
  static styles = [
    ...super.styles,
    projectInviteViewStyles,
  ];

  static properties = {
    ...super.properties,

    /**
     * Sub-view modes for the invite view.
     * 
     * - `ready`: Initial state, ready to invite.
     * - `sent-members`: Member invitation complete screen.
     */
    loadedViewMode: { type: String },

    memberSearchCache: { type: Object },
    memberList: { type: Array },

    selectedMembers: { type: Array },
    invitedMembers: { type: Array },
    role: { type: String },
    proposerNote: { type: String },

    sending: { type: Boolean },
    totalInvitationCount: { type: Number },
    sentInvitationCount: { type: Number },
    invitationErrors: { type: Array },
  };

  constructor() {
    super();

    this.loadedViewMode = 'ready';

    this.memberSearchCache = null;
    this.memberList = [];

    this.selectedMembers = [];
    this.invitedMembers = [];
    this.role = projectMemberRoleOptions[0].value; // default to the first role option
    this.proposerNote = '';

    this.sending = false;
    this.totalInvitationCount = null;
    this.sentInvitationCount = null;
    this.invitationErrors = [];
  }

  /*=== LIFECYCLE ===*/

  async projectLoaded() {
    // This view is for members only
    if (!this.context?.isMember) {
      const error = new Error('This page is only available to project members.');
      error.detail = { resultCode: 'view-not-allowed' };
      this.showToast('This page is only available to project members.', null, 'danger');
      throw error;
    }

    // Get the cache and wait for refresh if it's stale
    this.memberSearchCache = await this.appElement.cacheManager.getCache(
      MEMBER_SEARCH_CACHE, CACHE_MAX_AGE,
    );
    if (this.memberSearchCache.isNew || this.memberSearchCache.isStale) {
      this.memberSearchCache = await this.memberSearchCache.refreshComplete;
    }

    // Build the member list
    this.memberList = Object.values(this.memberSearchCache?.data?.members || {}).map((member) => {
      member = enrichMemberSearchItem(member);

      if (member.level === 'network') {
        member.disabled = true;
        member.disabled_label = 'Network Level';
        member.disabled_tooltip =
          'This member must upgrade to contributor before participating in projects';
      }

      if (
        this.project.membersActive?.includes(member.id)
        || this.project.organizers?.includes(member.ref)
      ) {
        member.disabled = true;
        member.disabled_label = 'Existing Member';
        member.disabled_tooltip = 'This member is already a member of this project';
      }

      if (this.project.membersPending?.includes(member.id)) {
        member.disabled = true;
        member.disabled_label = 'Pending Member';
        member.disabled_tooltip = 'This member already has a pending request or invitation';
      }

      return member;
    });
  }

  /*=== ACTIONS ===*/

  async inviteMoreMembers() {
    this.loadedViewMode = 'ready';
    this.invitedMembers = [];
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async sendInvitations() {
    const { proposeNewDoc, proposeNewVersion } = q1VersionFunctions;

    const inviteForm = this.renderRoot.getElementById('invite-member-form');
    if (!inviteForm.reportValidity()) {
      this.showToast(null, 'Please fill in all required fields to continue', 'danger');
      return;
    }
    if (!confirm("Please confirm that you're ready to send these invitations.")) return;

    // We add an extra count so that we can start with the progress bar non-empty
    this.totalInvitationCount = this.selectedMembers.length + 1;
    this.sentInvitationCount = 1;
    this.invitedMembers = [];
    this.invitationErrors = [];
    this.sending = true;

    // Use the view base helper method to center the progress bar
    this.scrollToCenterOf('#invite-card');

    // Send the invitations
    for (const member of this.selectedMembers) {
      try {
        // If this is a prior member at inactive status, we use proposeNewVersion, else we use
        // proposeNewDoc. The params are the same for both functions.
        const proposeFunction = this.project.membersInactive?.includes(member.id)
          ? proposeNewVersion
          : proposeNewDoc;
        
        const response = await proposeFunction({
          flowKey: PROJECT_MEMBER_INVITE_FLOW,
          relatedDocIds: {
            projectId: this.project.id,
            memberId: member.id,
          },
          proposerNote: this.proposerNote,
          opParams: {
            role: this.role,
          },
        });
        
        // Customize the q1-member-chip link to point to the version doc
        const versionId = response.data?.versionId;
        member.chipLink = `/v/${versionId}`;

        // Also disable this member in the select list in case they decide to invite more.
        // If they do a full refresh, the pending member id list on the project should be populated
        // by that time.
        member.disabled = true;
        member.disabled_label = 'Pending Member';
        member.disabled_tooltip ='This member already has a pending request or invitation';

        this.invitedMembers = [ ...this.invitedMembers, member ]; // save for display in sent panel
      } catch (error) {
        console.error('Error proposing new doc', error);
        this.invitationErrors = [ ...this.invitationErrors, { member, error } ];
      } finally {
        this.sentInvitationCount += 1;
      }
    }

    const allInvitesFailed = this.invitedMembers.length === 0;
    const someInvitesFailed = this.invitationErrors.length > 0;
    if (allInvitesFailed) {
      this.showToast(
        'It looks like something went wrong. Please try again and '
          + 'reach out to the foundation team if you need help.',
        'All of your invitations failed to send!',
        'danger',
      );
    } else {
      inviteForm.reset(); // this will reset the form and clear the selected members
      if (someInvitesFailed) {
        this.showToast(
          `There may be a system error. `
            + `Please review the errors and reach out to the foundation team if you need help.`,
          'It looks like some of your invitations failed',
          'warning',
          10000,
        );
      } else {
        this.showToast(null, 'All invitations have been sent successfully!', 'success');
      }

      this.loadedViewMode = 'sent-members';
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.sending = false;
  }

  /*=== TAB ROUTE IMPLEMENTATION METHODS ===*/

  _getTabPath(tabKey) {
    if (!this.project) return window.location.pathname;

    return TAB_SETTINGS(this.project)[tabKey]?.path || TAB_SETTINGS(this.project).members.path;
  }

  _getTabTitle(tabKey) {
    if (!this.project) return 'Invite to Project - q1 Network';

    return TAB_SETTINGS(this.project)[tabKey]?.title || 'Invite to Project - q1 Network';
  }

  /*=== HANDLERS ===*/

  // None yet

  /*=== GETTERS ===*/

  get invitationProgress() {
    if (!this.sentInvitationCount || !this.totalInvitationCount) return 0;
    
    return (this.sentInvitationCount / this.totalInvitationCount) * 100;
  }

  get invitationErrorText() {
    return JSON.stringify(
      (this.invitationErrors || []).map(({ member, error }) => ({
        member,
        error: !error?.message ? 'Unknown error' : {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
      })),
      null,
      2,
    );
  }

  get joinLinkEnabled() {
    return this.project?.visibility !== 'private';
  }

  get canInvite() {
    return this.context?.allowedActions?.includes('invite') || false;
  }

  get canEdit() {
    return this.context?.allowedActions?.includes('edit') || false;
  }

  get joinViewPath() {
    if (!this.project?.detailViewPath) return '';
    return `${this.project.detailViewPath}/join`;
  }

  get joinLink() {
    return `${location.origin}${this.joinViewPath}?rm=${this.currentMember.number}`;
  }

  /*=== TEMPLATES ===*/

  get contentHeaderTemplate() {
    if (['init', 'loaded'].includes(this.viewMode)) return null;

    return html`
      <header-bar class="with-border">
        <h1>
          <span class="prefix">q1 Network Project</span>
          Project Key: ${this.projectKey || '[ None ]'}
        </h1>
      </header-bar>
    `;
  }
  
  get viewModeTemplate_loaded() {
    return html`
      ${this.loadedHeaderTemplate}

      ${!this.canInvite || (this.project?.status !== 'live') ? '' : html`
        <div id="invite-card-wrapper">
          <div id="invite-card" class="sl-themed-secondary">
            <sl-tab-group
              id="project-tabs"
              @sl-tab-show=${this.handleTabShow}
              data-tab-route="source"
            >
              <sl-tab
                slot="nav"
                panel="members"
              >Invite Members</sl-tab>
              
              <sl-tab-panel name="members">
                ${this.loadedViewMode === 'sent-members'
                  ? this.sentMembersTemplate
                  : this.inviteMembersTemplate
                }
              </sl-tab-panel>
            </sl-tab-group>
            
            <q1-progress-panel
              label="Sending invitations..."
              value=${this.invitationProgress}
              ?open=${this.sending}
            ></q1-progress-panel>
          </div>
        </div>
      `}
    `;
  }

  get loadedHeaderTemplate() {
    return html`
      <project-invite-header>
        <q1-project-card .project=${this.project} always-active no-bg></q1-project-card>
      </project-invite-header>

      ${this.activationStatusPanelTemplate}
      ${this.inviteLinkPanelTemplate}
    `;
  }

  get activationStatusPanelTemplate() {
    if (this.project?.status === 'live') return null;

    return html`
      <activation-status-panel>
        <h2>Publish Project to Enable Invitations</h2>
        <p>
          The project is currently at <strong>${this.project?.status_label}</strong> status, 
          which means it cannot be joined by new members. 
          
          Publish the project to enable invitation of new members.
        </p>
      </activation-status-panel>
    `;
  }

  get inviteLinkPanelTemplate() {
    if (this.project?.status !== 'live') return null;

    return html`
      <invite-link-panel class=${this.joinLinkEnabled ? '' : 'disabled'}>
        <header-bar class="nested align-center">
          <h2>Join Link</h2>

          ${this.joinLinkEnabled ? '' : html`
            <sl-tag size="large">
              <sl-icon name="link_off" library="material"></sl-icon>
              Disabled
            </sl-tag>
          `}
        </header-bar>

        ${this.joinLinkEnabled ? html`
          ${this.canInvite ? html`
            <span>
              As an organizer, you can use the form below to invite existing q1 Network members 
              directly into the project.
            </span>
            <span>
              You can also share a link to the project's
              <a href=${this.joinViewPath}>join page</a>
              with potential members, even if they are
              not yet members of the q1 Network.
            </span>
          ` : html`
            <span>
              Only organizers can invite members directly into the project.
            </span>
            <span>
              But you can share a link
              to the project's
              <a href=${this.joinViewPath}>join page</a>
              with potential members, even if they are
              not yet members of the q1 Network.
            </span>
          `}

          <copy-button-wrapper>
            <sl-input
              id="invite-link"
              value=${this.joinLink}
              label="Your Personalized Referral Link"
              help-text="Share this link to get credited as the referrer for new members who join."
              disabled
            ></sl-input>

            <sl-copy-button from="invite-link.value"></sl-copy-button>
          </copy-button-wrapper>
        ` : html` <!-- not joinLinkEnabled -->
          <span>
            The project visibility is currently set to <strong>private</strong>, which disables
            the join link. Members must be invited directly.
          </span>
          
          ${!this.canEdit ? html`
            <span>
              <strong>Note:</strong> Only organizers can invite members directly into the project.
            </span>
          ` : html`
            <span>
              You can change the visibility in the
              <a href="${this.project.detailViewPath}/manage/settings">project settings</a>.
            </span>
          `}
        `}
      </invite-link-panel>
    `;
  }
  
  get inviteMembersTemplate() {
    return html`
      <form id="invite-member-form" class="q1 sl-themed-secondary">
        
        <p class="subtitle hero primary">
          The form below lets you invite existing q1 Network members to your project.
          Use the share link above for everyone else.
        </p>

        <p>
          <strong>Note:</strong> For now you can only invite <em>existing</em> members of the 
          q1 Network who are at the <em>contributor</em> level of membership.
          You can invite up to 5 members at a time.
        </p>

        ${this.invitationErrorsTemplate}

        <sl-radio-group
          id="role"
          name="role"
          label="Project Role"
          value=${this.role}
          @sl-change=${this.handleChange}
          help-text="Choose the role you want to invite into"
          required
        >
          ${projectMemberRoleOptions.filter(
            o => o.invitableBy.includes(this.context?.role)
          ).map((option) => html`
            <sl-radio-button value=${option.value}>
              <sl-icon slot="prefix" library="material" name=${option.icon}></sl-icon>
              ${option.label}
            </sl-radio-button>
          `)}
        </sl-radio-group>

        <q1-md-textarea
          id="proposerNote"
          name="proposerNote"
          label="Invitation Message"
          @change=${this.handleChange}
          maxlength="5000"
          required
        ></q1-md-textarea>

        <q1-member-select-list
          .memberList=${this.memberList}
          id="selectedMembers"
          name="selectedMembers"
          label="Members to Invite"
          @change=${this.handleChange}
          maxlength="5"
          required
        ></q1-member-select-list>

        <button-bar class="sl-themed-secondary right-align">
          ${this.selectedMembers.length === 0 ? html`
            <span class="disabled-help-text">
              Please select at least one member to invite
            </span>
          ` : ''}

          <sl-button
            id="send-invitations"
            variant="primary"
            size="large"
            ?disabled=${this.selectedMembers.length === 0}
            @click=${this.sendInvitations}
          >
            <sl-icon slot="prefix" library="material" name="send"></sl-icon>
            ${this.selectedMembers.length === 1 ? html`Send Invitation` : html`Send Invitations`}
          </sl-button>
        </button-bar>
      </form>
    `;
  }

  get sentMembersTemplate() {
    return html`
      <div class="sent-panel">
        <h2>
          ${this.invitedMembers.length}
          ${this.invitedMembers.length === 1
            ? 'project member invitation sent!'
            : 'project member invitations sent!'
          }
        </h2>  
        <p class="subtitle hero">
          Your invitees should receive notifications by email and Slack, but it's probably a good
          idea to reach out to them directly as well. We're still in beta and are building up
          email deliverability / reputation.
        </p>
        <p>
          When reaching out to your invitees, you can tell them to go to
          <a href="https://q1.is/inbox" target="_blank">https://q1.is/inbox</a>
          to see your invitation.
        </p>

        ${this.invitationErrorsTemplate}

        <h3>Invited Members</h3>

        <invited-members-list>
          ${repeat(this.invitedMembers, (member) => member.id, member => html`
            <q1-member-chip
              .member=${member}
              target="_blank"
              extra-fields="number"
              icon=${member.level_icon}
              icon-tooltip=${member.level_label}
              signed-in show-linkedin
            ></q1-member-chip>
          `)}
        </invited-members-list>

        <sl-divider class="full-width"></sl-divider>

        <button-bar class="sl-themed-secondary right-align">
          <sl-button
            id="invite-more-members-button"
            variant="primary"
            size="large"
            @click=${this.inviteMoreMembers}
          >
            <sl-icon slot="prefix" library="material" name="person_add"></sl-icon>
            Invite More Members
          </sl-button>
        </button-bar>
      </div>
    `;
  }

  get invitationErrorsTemplate() {
    if (!this.invitationErrors?.length) return '';

    return html`
      <sl-divider class="full-width"></sl-divider>

      <h3 class="error">⚠️ Invitation Errors ⚠️</h3>

      <p class="subtitle error">
        The following errors occurred while sending invitations. Please review, try again,
        and reach out to the foundation team if you need help.

        <span class="copy-link">
          <sl-copy-button .value=${this.invitationErrorText}></sl-copy-button>
          <sl-icon library="material" name="arrow_back"></sl-icon>
          Click here to copy error data to send to us.
        </span>
      </p>

      <invitation-error-list>
        ${map(this.invitationErrors, item => html`
          <error-item>
            <q1-member-chip
              .member=${item.member}
              target="_blank"
              extra-fields="number"
              icon=${item.member.level_icon}
              icon-tooltip=${item.member.level_label}
              signed-in
            ></q1-member-chip>
            <div class="error-message">
              <strong>${item.error?.name || 'Error Message'}:</strong>
              ${item.error?.message || 'Unknown error occurred'}
            </div>
          </error-item>
        `)}
      </invitation-error-list>

      <sl-divider class="full-width"></sl-divider>
    `;
  }

}

customElements.define('project-invite-view', ProjectInviteView);
